import React, { useEffect, useState } from "react";

import { css } from "@emotion/react";
import { HeadFC, Link, PageProps, Script } from "gatsby";
import Layout from "../components/Layout";
import GridContainer from "@upsun/react-component-library/src/components/GridContainer";
import GridCol from "@upsun/react-component-library/src/components/GridCol";
import { SEO } from "../components/Seo";
import GreenhouseForm from "../components/jobs/GreenhouseForm";
import Heading from "@upsun/react-component-library/src/components/Heading";
import { pxToRem } from "@upsun/react-component-library/src/styles";

const Job: React.FC<PageProps> = ({ location }) => {
  const params = new URLSearchParams(location.search);
  const defaultJobData = {
    title: "",
    content: "",
    location: {
      name: "",
    },
    departments: [
      {
        name: "",
      },
    ],
    questions: [
      {
        label: "",
        required: true,
        fields: [
          {
            name: "",
            type: "",
            values: [],
          },
        ],
      },
    ],
  };
  const [jobId] = useState(params.get("gh_jid"));
  const [submit, setSubmit] = useState(0);
  const [jobData, setJobData] = useState(defaultJobData);

  useEffect(() => {
    if (jobId) {
      fetch(
        `https://boards-api.greenhouse.io/v1/boards/platformsh/jobs/${jobId}?questions=true`
      )
        .then((response) => {
          if (response.status === 200) {
            return response.json();
          } else {
            return defaultJobData;
          }
        })
        .then((results) => {
          setJobData(results);
        })
        .catch((error) => {
          console.error("An error happened fetching greenhouse data: ", error);
        });
    }
  }, [jobId && jobId !== "0"]);

  const decodeHtml = (content: any) => {
    return content
      .replaceAll("&lt;", "<")
      .replaceAll("&gt;", ">")
      .replaceAll("&quot;", '"')
      .replaceAll("&amp;", "&")
      .replaceAll("style", "data-style")
      .replaceAll("&nbsp;", "<p></p>");
  };

  const contentStyles = css`
    ul,
    ol {
      margin-bottom: 2rem;
    }
  `;

  return (
    <Layout location={location.pathname}>
      <Script src="https://www.google.com/recaptcha/enterprise.js?render=6LfDgmEpAAAAAL1uvkI2L8bHGOSBlGpyKkOlLzOZ" />
      <GridContainer>
        {submit === 0 && (
          <GridCol
            colStart={1}
            colEnd={13}
            colStartMd={2}
            colEndMd={12}
            colStartLg={3}
            colEndLg={11}
          >
            <Link to={"/jobs/"}>All jobs</Link>
            <Heading level={1} text={jobData.title} />
            <p>
              {jobId && (
                <>
                  <strong>Location:</strong> {jobData.location.name}
                </>
              )}
              {!jobId && (
                <p
                  css={css`
                    text-align: center;
                  `}
                >
                  <strong>
                    Invalid job id, click <a href="/jobs/">here</a> to go back
                    to the job board!
                  </strong>
                </p>
              )}
            </p>
            <div
              css={contentStyles}
              dangerouslySetInnerHTML={{ __html: decodeHtml(jobData.content) }}
            />
            {jobId && jobId !== "0" && (
              <GreenhouseForm
                form={jobData.questions}
                jobId={jobId}
                setSubmit={setSubmit}
              />
            )}
          </GridCol>
        )}
        {submit !== 0 && (
          <GridCol
            colStart={1}
            colEnd={13}
            colStartMd={2}
            colEndMd={12}
            colStartLg={3}
            colEndLg={11}
          >
            <Link to={"/jobs/"}>View more jobs at Platform.sh</Link>
            {submit === 200 && (
              <>
                <Heading
                  level={1}
                  text={"Thank you for applying"}
                  css={css`
                    margin-top: ${pxToRem(48)};
                  `}
                />
                <p>
                  Thank you for your interest in our company and open positions.
                </p>
                <p>
                  Your application has been received. If there is a fit, someone
                  will be getting back to you within 2 weeks.
                </p>
                <p>
                  In the meantime, do not hesitate and setup your first project
                  with Upsun; your free trial is{" "}
                  <a href="https://auth.upsun.com/register/">here</a>.
                </p>
              </>
            )}
            {submit !== 200 && (
              <>
                <Heading
                  level={1}
                  text={"Oh no!"}
                  css={css`
                    margin-top: ${pxToRem(48)};
                  `}
                />
                <p>
                  Looks like something went wrong processing your application,
                  please try again.
                </p>
              </>
            )}
            <a href={`/job/?gh_jid=${jobId}`}>Back to application</a>
          </GridCol>
        )}
      </GridContainer>
    </Layout>
  );
};

export const Head: HeadFC = ({ location }) => (
  <SEO
    title={`Job application | Upsun`}
    description={
      "Join a global, remote-first team committed to fairness, sustainability, and your career growth. Discover your next role at Upsun"
    }
    pathname={location.pathname}
  />
);

export default Job;
